import type { ConteggioSessione } from '@eventi/assegna.ts';
import type { AulaEvento, ProgrammaEvento, SessioneEvento } from '@eventi/tipi.ts';
import { cn } from '../../lib/cn';
import { livelloSessione, motivoLivello, type LivelloSessione } from '../../lib/eventi/assegnazioni';
import { GrigliaGiorno } from './GrigliaGiorno';
import { Pannello } from './Pannello';
import { SceltaGiorno } from './SceltaGiorno';
import { useGiornoScelto } from './useGiornoScelto';

type Props = {
  programma: ProgrammaEvento;
  conteggi: Map<string, ConteggioSessione>;
  /** La sessione scelta per filtrare l'elenco sotto, se c'e'. */
  sessioneScelta: string | null;
  onScegliSessione: (codice: string | null) => void;
};

const COLORI: Record<LivelloSessione, string> = {
  oltre: 'border-red-300 bg-red-50',
  pieno: 'border-amber-300 bg-amber-50',
  ok: 'border-gray-200 bg-white',
  senza_limite: 'border-gray-200 bg-white',
};

const BARRA: Record<LivelloSessione, string> = {
  oltre: 'bg-red-500',
  pieno: 'bg-amber-500',
  ok: 'bg-green-500',
  senza_limite: 'bg-neutral-300',
};

const VUOTO: ConteggioSessione = { richiesti: 0, assegnati: 0, confermati: 0, posti: null };

function CellaSessione({ sessione, conteggio, scelta, onScegli }: {
  sessione: SessioneEvento;
  conteggio: ConteggioSessione;
  scelta: boolean;
  onScegli: () => void;
}) {
  const livello = livelloSessione(conteggio);
  const { richiesti, assegnati, posti } = conteggio;
  const richiesteOltre = posti !== null && richiesti > posti;
  const riempimento = posti === null ? 0 : posti === 0 ? 100 : Math.min(100, (assegnati / posti) * 100);
  return (
    <button
      type="button"
      onClick={onScegli}
      aria-pressed={scelta}
      title={`${motivoLivello(conteggio)}. Tocca per vedere chi c'è.`}
      className={cn(
        'flex h-full w-full flex-col rounded-lg border p-2.5 text-left transition-shadow hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        COLORI[livello],
        scelta && 'ring-2 ring-primary-500',
      )}
    >
      <span className="block text-[12px] font-medium leading-snug text-gray-900 line-clamp-2">{sessione.titolo}</span>
      <span className="mt-auto flex items-baseline justify-between gap-2 pt-2">
        <span className="text-sm font-bold tabular-nums text-gray-900">
          {assegnati}
          <span className="font-medium text-neutral-400">/{posti ?? '∞'}</span>
          <span className="ml-1 text-[10px] font-normal text-neutral-500">assegnati</span>
        </span>
        <span className={cn('text-[11px] tabular-nums', richiesteOltre ? 'font-semibold text-red-700' : 'text-neutral-500')}>
          {richiesti} {richiesti === 1 ? 'richiesta' : 'richieste'}
        </span>
      </span>
      <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-gray-200/70">
        {posti !== null && <span className={cn('block h-full rounded-full', BARRA[livello])} style={{ width: `${riempimento}%` }} />}
      </span>
    </button>
  );
}

function IntestazioneAula({ aula }: { aula: AulaEvento }) {
  return (
    <div className="flex h-full flex-col justify-center rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
      <span className="truncate text-sm font-semibold text-gray-900" title={aula.nome}>
        {aula.nome}
      </span>
      <span className="text-[11px] text-neutral-500">{aula.posti === null ? 'senza limite' : `${aula.posti} posti`}</span>
    </div>
  );
}

function Legenda() {
  const voci = [
    { colore: BARRA.ok, testo: 'posti liberi' },
    { colore: BARRA.pieno, testo: 'aula piena' },
    { colore: BARRA.oltre, testo: 'oltre i posti (assegnati o richieste)' },
  ];
  return (
    <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-neutral-500">
      {voci.map((v) => (
        <span key={v.testo} className="inline-flex items-center gap-1.5">
          <span className={cn('h-2 w-2 rounded-full', v.colore)} /> {v.testo}
        </span>
      ))}
    </p>
  );
}

/**
 * Il tabellone: un giorno alla volta, le aule in riga e i turni in colonna,
 * con assegnati su posti e richieste per ogni incontro. Rosso quando
 * assegnati o richieste superano i posti. Una sessione lunga occupa tutti i
 * suoi turni in un riquadro solo.
 */
export function TabelloneAule({ programma, conteggi, sessioneScelta, onScegliSessione }: Props) {
  const [giorno, setGiorno] = useGiornoScelto(programma.giorni);
  return (
    <Pannello
      titolo="Tabellone delle aule"
      sotto="Per ogni incontro: quanti sono assegnati sui posti dell'aula e quanti l'hanno chiesto. Tocca un incontro per vedere chi c'è nell'elenco qui sotto."
      azioni={<SceltaGiorno giorni={programma.giorni} attivo={giorno} onCambia={setGiorno} />}
    >
      <GrigliaGiorno
        programma={programma}
        giorno={giorno}
        includi={(s) => s.sceglibile}
        intestazioneAula={(aula) => <IntestazioneAula aula={aula} />}
        cella={(s) => (
          <CellaSessione
            sessione={s}
            conteggio={conteggi.get(s.codice) ?? VUOTO}
            scelta={sessioneScelta === s.codice}
            onScegli={() => onScegliSessione(sessioneScelta === s.codice ? null : s.codice)}
          />
        )}
        vuoto="In questo giorno non c'è niente da scegliere."
      />
      <Legenda />
    </Pannello>
  );
}
